import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import * as api from '../lib/api';
import type { GlobalSearchResult, GlobalSearchSource, SelectedGlobalContext } from '../types';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 120;
  display: grid;
  place-items: start center;
  padding: 88px 20px 24px;
  background: rgba(4,8,17,0.58);
`;

const Dialog = styled.div`
  width: min(720px, 100%);
  max-height: min(720px, calc(100vh - 112px));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid ${p => p.theme.border};
  border-radius: 18px;
  background: ${p => p.theme.surface};
  color: ${p => p.theme.text};
  box-shadow: 0 24px 80px rgba(0,0,0,0.38);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  border-bottom: 1px solid ${p => p.theme.border};
`;

const TitleBlock = styled.div`
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0;
  font-family: 'Raleway', system-ui, sans-serif;
  font-size: 20px;
  letter-spacing: -0.02em;
`;

const Subtitle = styled.div`
  margin-top: 5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${p => p.theme.muted};
  font-size: 13px;
`;

const CloseButton = styled.button`
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border: 1px solid ${p => p.theme.border};
  border-radius: 10px;
  background: ${p => p.theme.surfaceStrong};
  color: ${p => p.theme.text};
  font-size: 18px;
  font-weight: 800;

  &:hover { background: ${p => p.theme.primarySoft}; }
`;

const Body = styled.div`
  padding: 16px 20px 20px;
  overflow-y: auto;
`;

const SearchInput = styled.input`
  width: 100%;
  min-width: 0;
  padding: 12px 14px;
  border: 1px solid ${p => p.theme.border};
  border-radius: 10px;
  background: ${p => p.theme.surfaceStrong};
  color: ${p => p.theme.text};
  outline: none;

  &::placeholder { color: ${p => p.theme.muted}; }

  &:focus {
    border-color: ${p => p.theme.primary};
    box-shadow: 0 0 0 3px ${p => p.theme.primarySoft};
  }
`;

const Status = styled.div`
  padding: 16px 2px 0;
  color: ${p => p.theme.muted};
  font-size: 13px;
`;

const Warning = styled(Status)`
  color: ${p => p.theme.warning};
`;

const Results = styled.div`
  display: grid;
  gap: 8px;
  margin-top: 14px;
`;

const ResultButton = styled.button`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 13px 14px;
  border: 1px solid ${p => p.theme.border};
  border-radius: 12px;
  background: ${p => p.theme.surfaceStrong};
  color: ${p => p.theme.text};
  text-align: left;

  &:hover { background: ${p => p.theme.primarySoft}; border-color: ${p => p.theme.primary}; }
`;

const Main = styled.div`
  min-width: 0;
`;

const ResultTitle = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 800;
`;

const ResultSubtitle = styled.div`
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${p => p.theme.muted};
  font-size: 12px;
`;

const Tags = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 7px;
  background: ${p => p.theme.surfaceSoft};
  color: ${p => p.theme.muted};
  font-size: 11px;
  font-weight: 800;
`;

type Props = {
  mode: 'company' | 'workspace' | 'user';
  context: SelectedGlobalContext;
  onSelect: (context: SelectedGlobalContext) => void;
  onClose: () => void;
};

function sourceLabel(source: GlobalSearchSource) {
  if (source === 'metabase') return 'Workspace';
  if (source === 'pipedrive') return 'Pipedrive';
  return 'PostHog';
}

function resultTags(result: GlobalSearchResult) {
  if (result.type === 'workspace') return ['Workspace'];
  if (result.type === 'user') {
    const sources = result.user.sources || [];
    const external = sources.filter(source => source !== 'metabase');
    if (external.length > 1) return ['Both'];
    return external.map(sourceLabel);
  }
  return ['Company'];
}

function resultToContext(result: GlobalSearchResult, current: SelectedGlobalContext): SelectedGlobalContext | null {
  if (result.type === 'company') {
    return {
      selectedType: current.selectedType,
      selectedAt: Date.now(),
      company: result.company,
      workspace: null,
      user: current.user || null,
    };
  }
  if (result.type === 'workspace') {
    return {
      selectedType: current.selectedType,
      selectedAt: Date.now(),
      company: result.company || current.company || null,
      workspace: result.workspace,
      user: current.user || null,
    };
  }
  if (result.type === 'user') {
    return {
      selectedType: current.selectedType,
      selectedAt: Date.now(),
      company: result.company || current.company || null,
      workspace: result.workspace || current.workspace || null,
      user: result.user,
    };
  }
  return null;
}

export function ContextSearchDialog({ mode, context, onSelect, onClose }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GlobalSearchResult[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const requestRef = useRef(0);
  const metabaseUserId = typeof context.user?.references?.metabaseUserId === 'string'
    ? context.user.references.metabaseUserId
    : context.user?.metabase?.userId || null;
  const userEmail = context.user?.email || context.user?.emails?.[0] || null;

  useEffect(() => {
    const requestId = ++requestRef.current;
    const term = query.trim();
    setIsLoading(true);
    setError('');
    const timeout = window.setTimeout(() => {
      const request = mode === 'company'
        ? userEmail || metabaseUserId ? api.searchUserCompanies({ email: userEmail, userId: metabaseUserId }, term) : Promise.reject(new Error('No user email or id available'))
        : mode === 'workspace'
          ? context.company?.id ? api.searchCompanyWorkspaces(context.company.id, term) : Promise.reject(new Error('No company selected'))
          : context.workspace?.id ? api.searchWorkspaceUsers(context.workspace.id, term) : Promise.reject(new Error('No workspace selected'));

      request
        .then(response => {
          if (requestRef.current !== requestId) return;
          const expected = mode === 'company' ? 'company' : mode === 'workspace' ? 'workspace' : 'user';
          setResults(response.results.filter(result => result.type === expected));
          setWarnings(response.warnings);
        })
        .catch(apiError => {
          if (requestRef.current !== requestId) return;
          setResults([]);
          setWarnings([]);
          setError(apiError.response?.data?.error || apiError.message || 'Search failed');
        })
        .finally(() => {
          if (requestRef.current === requestId) setIsLoading(false);
        });
    }, term ? 250 : 0);

    return () => window.clearTimeout(timeout);
  }, [context.company?.id, context.workspace?.id, metabaseUserId, mode, query, userEmail]);

  function selectResult(result: GlobalSearchResult) {
    const next = resultToContext(result, context);
    if (!next) return;
    onSelect(next);
    onClose();
  }

  const title = mode === 'company' ? 'Select company' : mode === 'workspace' ? 'Select workspace' : 'Select user';
  const subtitle = mode === 'company'
    ? context.user?.name || context.user?.email || 'Current user'
    : mode === 'workspace'
    ? context.company?.name || 'Current company'
    : context.workspace?.name || 'Current workspace';
  const placeholder = mode === 'company' ? 'Filter this user\'s companies by name, id, or client key' : mode === 'workspace' ? 'Filter workspaces by name, id, or token' : 'Filter workspace users by name, email, or id';

  return (
    <Overlay onMouseDown={onClose}>
      <Dialog role="dialog" aria-modal="true" aria-label={title} onMouseDown={event => event.stopPropagation()}>
        <Header>
          <TitleBlock>
            <Title>{title}</Title>
            <Subtitle title={subtitle}>{subtitle}</Subtitle>
          </TitleBlock>
          <CloseButton type="button" aria-label="Close dialog" onClick={onClose}>x</CloseButton>
        </Header>
        <Body>
          <SearchInput
            autoFocus
            value={query}
            onChange={event => setQuery(event.target.value)}
            onKeyDown={event => { if (event.key === 'Escape') onClose(); }}
            placeholder={placeholder}
          />
          {warnings.map(warning => <Warning key={warning}>{warning}</Warning>)}
          {error && <Status>{error}</Status>}
          {!error && isLoading && <Status>Searching...</Status>}
          {!error && !isLoading && !results.length && <Status>No results found.</Status>}
          {!error && results.length > 0 && (
            <Results>
              {results.map(result => (
                <ResultButton key={result.id} type="button" onClick={() => selectResult(result)}>
                  <Main>
                    <ResultTitle title={result.label}>{result.label}</ResultTitle>
                    {result.subtitle && <ResultSubtitle title={result.subtitle}>{result.subtitle}</ResultSubtitle>}
                  </Main>
                  <Tags>{resultTags(result).map(tag => <Tag key={tag}>{tag}</Tag>)}</Tags>
                </ResultButton>
              ))}
            </Results>
          )}
        </Body>
      </Dialog>
    </Overlay>
  );
}
